<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    style="z-index: 1100 !important;"
  >
    <template v-slot:activator="{ props }">
      <slot name="activator" v-bind="{ props }" />
    </template>

    <v-form ref="form" data-cy="dialog-convertVideo-modal">
      <v-card>
        <v-card-title
          class="d-flex justify-space-between"
          data-cy="dialog-convertVideo-title"
        >
          {{ $t('routes.pod.menuMediaConverter.convertVideoFile') }}
          <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text scrollable class="pt-4 scrollable">
          <div data-cy="podMenu-tools-convertVideo-selectFormat">
            <v-select
              v-model="selectedFormat"
              v-bind:items="videoFormats"
              v-bind:label="$t('routes.pod.menuMediaConverter.selectFormat')"
              v-bind:rules="selectedFormatRules"
              :menu-props="{ maxHeight: 320 }"
              :item-props="() => ({ tabindex: 0 })"
              variant="solo-filled"
              flat required single-line
            />
          </div>

          <div data-cy="podMenu-tools-convertVideo-selectResolution">
            <v-select
              v-model="selectedResolution"
              v-bind:items="videoResolution"
              v-bind:label="$t('routes.pod.menuMediaConverter.selectResolution')"
              v-bind:rules="selectedResolutionRules"
              item-title="name"
              item-value="id"
              :menu-props="{ maxHeight: 320 }"
              :item-props="() => ({ tabindex: 0 })"
              variant="solo-filled"
              flat required single-line return-object
            />
          </div>

          <div data-cy="podMenu-tools-convertVideo-selectVideoCodec">
            <v-select
              v-model="selectedVideoCodec"
              v-bind:items="videoCodec"
              v-bind:label="$t('routes.pod.menuMediaConverter.selectVideoCodec')"
              v-bind:rules="selectedVideoCodecRules"
              item-title="name"
              item-value="id"
              :menu-props="{ maxHeight: 320 }"
              :item-props="() => ({ tabindex: 0 })"
              variant="solo-filled"
              flat required single-line return-object
            />
          </div>

          <div data-cy="podMenu-tools-convertVideo-selectAudioCodec">
            <v-select
              v-model="selectedAudioCodec"
              v-bind:items="audioCodec"
              v-bind:label="$t('routes.pod.menuMediaConverter.selectAudioCodec')"
              v-bind:rules="selectedAudioCodecRules"
              item-title="name"
              item-value="id"
              :menu-props="{ maxHeight: 320 }"
              :item-props="() => ({ tabindex: 0 })"
              variant="solo-filled"
              flat required single-line return-object
            />
          </div>

          <UtilityPodsErrors class="mt-2"></UtilityPodsErrors>

          <div v-show="successMessage" class="bg-success" data-cy="convertFile-successMessage">
            {{ successMessage }}
          </div>
          <div v-show="errorMessage" class="bg-error" data-cy="convertFile-errorMessage">
            {{ errorMessage }}
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            v-bind:disabled="isRunJobsDisabled"
            class="bg-primary"
            variant="flat"
            @click="runJobs"
            data-cy="dialog-convertFile-ok"
          >
            {{ $t('routes.pod.menuMediaConverter.convert') }}
          </v-btn>
          <v-btn variant="text" @click="dialog = false" data-cy="dialog-convertFile-false">
            {{ $t('common.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>

  </v-dialog>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useServiceStore } from '@/stores/ServiceStore.js'
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import DialogModel from '@/components/mixin/DialogModel.js';

export default {
  mixins: [DialogActionKeys, DialogModel],
  name: "ConvertVideoFileDialog",
  props: {
    pods: {
      type: Array,
      required: true
    },
  },
  data: function() {
    return {
      selectedVideoCodec: null,
      selectedAudioCodec: null,
      selectedResolution: null,
      selectedFormat: null,

      errorMessage: "",
      successMessage: "",
      preventDoubleClick: false,
      selectedFormatRules: [
        v => !!v || this.$t('routes.pod.menuMediaConverter.errorSelectedFileFormat')
      ],
      selectedAudioCodecRules: [
        v => !!v || this.$t('routes.pod.menuMediaConverter.errorSelectedAudioCodec')
      ],
      selectedResolutionRules: [
        v => !!v || this.$t('routes.pod.menuMediaConverter.errorSelectedResolution')
      ],
      selectedVideoCodecRules: [
        v => !!v || this.$t('routes.pod.menuMediaConverter.errorSelectedVideoCodec')
      ],
    };
  },
  mounted: function() {
    const serviceStore = useServiceStore();
    serviceStore.resetUtilityPods();
  },
  watch: {
    dialog(newValue) {
      this.resetForm();

      if (newValue) {
        this.updatePodsUtility();
      }
    },
  },
  methods: {
    resetForm() {
      if (this.$refs.form) {
        this.$refs.form.reset();
      }

      this.$nextTick(() => {
        this.errorMessage = '';
        this.successMessage = '';
        this.preventDoubleclick = false;

        const serviceStore = useServiceStore();
        serviceStore.resetUtilityPods();
      });
    },
    updatePodsUtility() {
      this.errorMessage = '';
      this.errorTranslatable = '';

      if (this.suitablePods.length === 0) {
        const serviceStore = useServiceStore();
        serviceStore.resetUtilityPods();
        return;
      }

      const payload = {
        serviceAlias: this.mediaConverterService.alias,
        pods: this.suitablePods,
      };

      const serviceStore = useServiceStore();
      serviceStore.updatePodsUtility(payload);
    },
    runJobs: async function() {
      const validForm = await this.$refs.form.validate();
      if (!validForm.valid) {
        return;
      }

      this.errorMessage = "";
      this.successMessage = "";

      this.preventDoubleclick = true;
      setInterval(() => {
        this.preventDoubleclick = false;
      }, 10000);

      let payload = {
        serviceAlias: this.mediaConverterService.alias,
        utilityPods: this.runnablePods,
        params: {
          format: this.selectedFormat,
          resolution: this.selectedResolution.value,
          videocodec: this.selectedVideoCodec.value,
          audiocodec: this.selectedAudioCodec.value,
        }
      };

      const serviceStore = useServiceStore();
      try {
        await serviceStore.runService(payload);
        if (this.utilityPodsErrors.length === 0) {
          this.successMessage = this.$t('routes.pod.menuCreateTranscript.operationQueued');

          setInterval(() => {
            this.successMessage = '';

            if (this.dialog === true) {
              this.updatePodsUtility();
            }
          }, 10000);
        }
      } catch (error) {
        if (error && error.message) {
          this.errorMessage = this.$localizeMessage(error.message);
        } else {
          console.log(error);
          this.errorMessage = this.$t('backend.system-error');
        }

        this.preventDoubleclick = false;
      }
    },
  },
  computed: {
    videoFormats: function() {
      return ['mp4', 'avi', 'mpeg', 'mov'];
    },
    videoResolution: function() {
      return [
        {
          name: "Same as source",
          value: "source",
        },{
          name: "HD 1080p (1920x1080)",
          value: "hd1080",
        },
        {
          name: "HD 480p (854x480)",
          value: "hd480",
        },
        {
          name: "HD 360p (640x360)",
          value: "hd360",
        },
        {
          name: "Mobile (320x240)",
          value: "mobile",
        },
      ];
    },
    videoCodec: function() {
      let codecs = [
        {
          name: "H.264/AVC",
          value: "h264avc",
        },
        {
          name: "MPEG-4",
          value: "mpeg4",
        },
        {
          name: "Apple ProRes 422 LT",
          value: "prores_aw",
        },
      ];

      // Apple ProRes is available only for mov formats
      if (this.selectedFormat !== 'mov') {
        codecs.pop();
      }

      return codecs;
    },
    audioCodec: function() {
      let formats = [
        {
          name: "AAC",
          value: "aac",
        },
        {
          name: "MP3",
          value: "mp3",
        },
        {
          name: this.$t('routes.pod.menuMediaConverter.noAudio'),
          value: "noaudio",
        },
      ];

      // remove AAC codec if mpeg is selected
      if (this.selectedFormat === 'mpeg') {
        formats.shift();
      }

      return formats;
    },

    mediaConverterService: function() {
      const serviceStore = useServiceStore();
      return serviceStore.getMediaConverter;
    },
    isRunJobsDisabled: function() {
      return this.utilityPodsErrors.length > 0 || this.preventDoubleClick === true;
    },
    runnablePods: function() {
      const serviceStore = useServiceStore();
      return serviceStore.getRunnablePods;
    },
    utilityPodsErrors: function() {
      const serviceStore = useServiceStore();
      return serviceStore.getUtilityPodsErrors;
    },
    suitablePods: function() {
      const podStore = usePodStore();
      return podStore.getSelectedVideoPods;
    },
  }
}
</script>

<style>

</style>
